// src/components/block/liveStreamManagement/RegieConfigModal.tsx
// v8.1 — onLoad restauré dans LoadModalProps.
//        LoadModal résout les médias (download + IndexedDB) puis appelle onLoad
//        avec un payload complet (url + instantMediaId dans chaque slot).
//        C'est LiveStreamManager (handleLoadConfig) qui appelle setImageSlots etc.
//        Sauvegarde : preparePayloadForSave vide url ET instantMediaId.
//        Suppression de restorePayloadMediaUrls (inutile cross-machine).

import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import JSZip from 'jszip';
import { Save, FolderOpen, Download, Library, Upload, Trash2, X } from 'lucide-react';

import { VideoSource, ImageSlot, SoundSlot, JingleSlot, LayoutSlot, VideoOverlaySlot, SourceAudioConfig } from '../../../contexts/MediaStreamContext';
import { AUTOSAVE_NAME } from '../../../backendServices/liveStreamManagment/regie_autosave';
import { isStreamedMediaUrl } from '../../../dataTypes/liveStreamManagment/utils_videoSource';
import { DeckKeyMap } from '../../../dataTypes/liveStreamManagment/types_deck';
import { MultiviewSlotConfig } from './MultiviewPanelV2';
import { SourceAliasMap } from './SourceAliasConfigModal';
import { StereoVolume, AudioEffectState } from './SourceConfigModal';
import type { VideoEffectsSettings } from './videoEffects/VideoEffectsPipelineManager';
import type { SavedExternalSources } from '../../../backendServices/liveStreamManagment/regie_external_sources';
import type { AutoDirectorSettings } from './autoDirector';
import type { StoredAudioSwitch } from '../../../dataTypes/liveStreamManagment/deck_persistence';
import type { WhipEncodingSettings } from '../../../dataTypes/liveStreamManagment/whipEncodingSettings';
import {
  saveRegieConfig, getRegieConfigs, deleteRegieConfig,
  uploadRegieConfigMedia, uploadRegieConfigSceneMedia,
  downloadRegieConfigZip, downloadRegieConfigMedia,
  RegieConfigRecord,
} from '../../../backendServices/liveStreamManagment/api_regieConfig';
import { pickSaveTarget, saveBlob, type SaveTarget } from '../../../backendServices/liveStreamManagment/save_file_picker';

import {
  preparePayloadForSave,
  storeMediaBlob,
} from '../../../backendServices/liveStreamManagment/local_media_store';

// ── Types ─────────────────────────────────────────────────────────────────────

export type RegieConfigFeature = 'image' | 'son' | 'video' | 'scene' | 'pip';

export const FEATURE_LABELS: Record<RegieConfigFeature, string> = {
  image : 'Image',
  son   : 'Son',
  video : 'Vidéo',
  scene : 'Scène',
  pip   : 'PiP',
};

interface SerializedDeckSlot {
  index:        number;
  id:           string;
  label:        string;
  type:         string;
  deviceId?:    string;
  externalUrl?: string;
  /** Source « audio seul » : image générée (noir, spectre, oscilloscope). */
  audioVisual?: string;
}

/** Les deux moitiés d'une sauvegarde de régie : le deck (pupitre) et le mediaboard (médias). */
export type RegieConfigKind = 'deck' | 'media';

export const KIND_LABELS: Record<RegieConfigKind, string> = { deck: 'Deck', media: 'Media' };

export interface RegieConfigPayload {
  /** Absent des sauvegardes antérieures à la scission : elles contiennent tout. */
  kind?      : RegieConfigKind;
  /** Présent seulement dans un ZIP exporté : identifiant technique du média → chemin du fichier (nom d'origine) dans le ZIP. */
  mediaFiles?: Record<string, string>;
  name       : string;
  channelUuid: string;
  /**
   * Chaîne où la sauvegarde a été FAITE. `channelUuid` est réécrit à l'enregistrement d'une
   * copie importée ; celui-ci ne l'est pas. Au chargement, c'est lui qui dit si l'on est dans
   * la régie de la chaîne d'origine ou dans celle d'une autre (smartphones et caméras RTMP
   * ne s'importent alors pas).
   */
  originChannelUuid?: string;
  date       : string;
  features   : { pupitre?: true }
               & Partial<Record<RegieConfigFeature, boolean>>
               & { withImages?: boolean; withSounds?: boolean; withVideos?: boolean; withScene?: boolean };
  data       : {
    pupitre?: {
      deckSlots             : SerializedDeckSlot[];
      /** Raccourcis du deck. Une famille de médias non sauvegardée (images, sons, vidéos, scènes) en est ABSENTE : au chargement, on garde celle du poste. */
      keyMap                : Partial<DeckKeyMap>;
      slotConfigs           : MultiviewSlotConfig[];
      sourceAliases         : SourceAliasMap;
      sourceAudioConfigs    : Record<string, SourceAudioConfig>;
      selectedAudioDeviceIds: Record<string, string>;
      stereoVolumes         : Record<string, StereoVolume>;
      audioEffectsMap       : Record<string, AudioEffectState>;
      /** Réglages fins des effets audio, par source. Absent des sauvegardes plus anciennes. */
      audioEffectConfigsMap?: Record<string, Record<string, unknown>>;
      /** Réglages vidéo (niveaux, effets actifs et leur config), par source. Absent des sauvegardes plus anciennes. */
      videoEffectsSettingsMap?: Record<string, VideoEffectsSettings>;
      /** Liste des sources externes de la chaîne (HLS, Xtream sans mot de passe, RTMP avec clé, smartphones avec identifiants). */
      externalSources?: SavedExternalSources;
      /** Mode de connexion des smartphones : 'sfu' (sécurisé) ou 'p2p' (direct). */
      deviceTransport?: 'p2p' | 'sfu';
      /** Sources que la régie connaissait à la sauvegarde (placées ou non) : au chargement, elles ne sont pas reposées d'office sur un bouton. */
      knownSourceIds?: string[];
      /** Réglages du réalisateur auto (sans son état allumé/éteint). Absent des sauvegardes plus anciennes : on garde ceux du poste. */
      autoDirector?: AutoDirectorSettings;
      /** Réglages d'encodage WHIP de la chaîne. Absent des sauvegardes plus anciennes : on garde ceux du poste. */
      whipEncoding?: WhipEncodingSettings;
      /** Switch audio : mode (Source / Fixe / Mixeur), source fixe, gains et voies coupées du mixeur. Absent des sauvegardes plus anciennes : on garde l'état courant. */
      audioSwitch?: StoredAudioSwitch;
    };
    image?: { slots: ImageSlot[] };
    son?:   { slots: SoundSlot[] };
    video?: { slots: JingleSlot[] };
    scene?: { slots: LayoutSlot[] };
    /** Emplacements PiP : de la config seule (webcam/appareil, position, cadre), pas de fichier. */
    pip?:   { slots: VideoOverlaySlot[] };
  };
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface SaveModalBase {
  mode         : 'save';
  channelName  : string;
  channelUuid  : string;
  userName     : string;
  imageSlots   : ImageSlot[];
  soundSlots   : SoundSlot[];
  jingleSlots  : JingleSlot[];
  layoutSlots  : LayoutSlot[];
  pipSlots     : VideoOverlaySlot[];
  onClose      : () => void;
}

interface DeckSaveModalProps extends SaveModalBase {
  kind         : 'deck';
  deckSlots              : (VideoSource | null)[];
  keyMap                 : DeckKeyMap;
  slotConfigs            : MultiviewSlotConfig[];
  sourceAliases          : SourceAliasMap;
  getSourceAudioConfig   : (id: string) => SourceAudioConfig;
  selectedAudioDeviceIds : Map<string, string>;
  stereoVolumes          : Map<string, StereoVolume>;
  audioEffectsMap        : Map<string, AudioEffectState>;
  audioEffectConfigsMap  : Map<string, Record<string, unknown>>;
  videoEffectsSettingsMap: Map<string, VideoEffectsSettings>;
  deviceTransport        : 'p2p' | 'sfu';
  knownSourceIds         : string[];
  autoDirectorSettings   : AutoDirectorSettings;
  whipEncoding           : WhipEncodingSettings;
  audioSwitchState       : StoredAudioSwitch;
  /** Relève la liste des sources externes de la chaîne ; null si le microservice ne répond pas. */
  getExternalSources     : () => Promise<SavedExternalSources | null>;
}

interface MediaSaveModalProps extends SaveModalBase {
  kind         : 'media';
}

type SaveModalProps = DeckSaveModalProps | MediaSaveModalProps;

interface LoadModalProps {
  mode        : 'load';
  kind        : RegieConfigKind;
  channelUuid : string;
  onLoad      : (payload: RegieConfigPayload) => void;
  onClose     : () => void;
}

export type RegieConfigModalProps = SaveModalProps | LoadModalProps;

// ── Helpers ───────────────────────────────────────────────────────────────────

const defaultName = (channelName: string, userName: string, kind: RegieConfigKind) => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${channelName} — ${userName} — ${pad(now.getDate())}/${pad(now.getMonth()+1)}/${now.getFullYear()} ${pad(now.getHours())}:${pad(now.getMinutes())} — ${KIND_LABELS[kind]}`;
};

const normalizeName = (n: string) => n.trim().toLowerCase();

/** Une sauvegarde de cette chaîne porte-t-elle déjà ce nom ? (la sauvegarde automatique ne compte pas) */
async function nameAlreadyUsed(channelUuid: string, name: string): Promise<boolean> {
  const all = await getRegieConfigs(channelUuid);
  const wanted = normalizeName(name);
  return all.some(c => c.name !== AUTOSAVE_NAME && normalizeName(c.name) === wanted);
}

/** Un fichier importé convient s'il contient ce que la fenêtre charge : un pupitre (deck) ou des médias. */
const acceptsFile = (c: RegieConfigPayload, kind: RegieConfigKind) =>
  kind === 'deck' ? !!c.data.pupitre : hasMediaFeatures(c);
const wrongFileMessage = (kind: RegieConfigKind) =>
  kind === 'deck' ? 'Ce fichier ne contient pas de deck (pupitre).' : 'Ce fichier ne contient pas de médias.';

const hasMediaFeatures = (c: RegieConfigPayload) =>
  !!(c.features.image || c.features.son || c.features.video || c.features.scene || c.features.pip);

/**
 * Une sauvegarde de DECK porte le pupitre ET les médias ; une sauvegarde de MEDIA
 * ne porte que les médias. La liste « médias » montre donc toute sauvegarde qui en
 * contient (on peut n'en recharger que les médias) ; la liste « deck » celles qui
 * ont un pupitre.
 */
function belongsToKind(c: RegieConfigPayload, kind: RegieConfigKind): boolean {
  return kind === 'deck' ? !!c.data.pupitre : hasMediaFeatures(c);
}

const formatDate = (iso: string) => {
  try {
    return new Date(iso).toLocaleString('fr-FR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' });
  } catch { return iso; }
};

function serializeDeckSlots(slots: (VideoSource | null)[]): SerializedDeckSlot[] {
  return slots.map((s, idx) => ({
    index:       idx,
    id:          s?.id         ?? '',
    label:       s?.label      ?? '',
    type:        s?.type       ?? '',
    deviceId:    s?.deviceId,
    ...(s?.type === 'audio' && s.audioVisual && s.audioVisual !== 'black' ? { audioVisual: s.audioVisual } : {}),
    // Pas d'URL : celle d'une source externe peut porter un identifiant ou un jeton, et
    // la source n'est de toute façon jamais recréée depuis la sauvegarde.
  }));
}

async function downloadJson(payload: RegieConfigPayload, filename: string, target?: SaveTarget | null): Promise<void> {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  await saveBlob(blob, filename, 'json', target);
}

// ── Export ZIP des médias ─────────────────────────────────────────────────────
//
// Un dossier par sauvegarde : son JSON + les fichiers sous leur NOM D'ORIGINE,
// rangés par famille. Le JSON porte `mediaFiles` (identifiant technique → chemin
// dans le dossier) : c'est ce qui permet à l'import de rattacher chaque fichier à
// son emplacement sans que le nom, lui, ait à porter l'information.

interface MediaRef { prefix: string; folder: string; originalName?: string; label?: string }

const safeFileName = (n: string) => n.replace(/[\\/:*?"<>|]/g, '_').trim() || 'sans-nom';

function extFromMime(mime: string): string {
  const sub = mime.split('/')[1]?.split(';')[0] ?? '';
  return sub === 'mpeg' ? 'mp3' : sub || 'bin';
}

function collectMediaRefs(cfg: RegieConfigPayload): MediaRef[] {
  const refs: MediaRef[] = [];
  const d = cfg.data;
  d.image?.slots.forEach(s => { if (s.hasMedia) refs.push({ prefix: `image_${s.id}_slot`, folder: 'Images', originalName: s.originalName, label: s.label }); });
  d.son?.slots.forEach(s   => { if (s.hasMedia) refs.push({ prefix: `sound_${s.id}_slot`, folder: 'Sons',   originalName: s.originalName, label: s.label }); });
  d.video?.slots.forEach(s => { if (s.hasMedia) refs.push({ prefix: `video_${s.id}_slot`, folder: 'Videos', originalName: s.originalName, label: s.label }); });
  d.scene?.slots.forEach(sl => {
    const t = sl.background.type as string;
    if (sl.background.hasMedia && (t === 'image' || t === 'video' || t === 'sound')) {
      refs.push({ prefix: `scene_${sl.id}_background`, folder: 'Scenes', originalName: sl.background.originalName, label: `${sl.label} fond` });
    }
    sl.incrustations.forEach(ic => {
      const it = ic.type as string;
      if (ic.hasMedia && (it === 'image' || it === 'video' || it === 'sound')) {
        refs.push({ prefix: `scene_${sl.id}_incrustation-${ic.id}`, folder: 'Scenes', originalName: ic.originalName, label: `${sl.label} ${ic.label}` });
      }
    });
  });
  return refs;
}

/** Télécharge un ZIP des sauvegardes de médias données. Retourne le nombre de fichiers inclus et de fichiers introuvables. */
async function exportMediaZip(entries: RegieConfigRecord[], target?: SaveTarget | null): Promise<{ files: number; missing: number }> {
  const zip = new JSZip();
  const usedFolders = new Set<string>();
  let files = 0, missing = 0;

  for (const entry of entries) {
    let folder = safeFileName(entry.name), n = 2;
    while (usedFolders.has(folder)) folder = `${safeFileName(entry.name)} (${n++})`;
    usedFolders.add(folder);

    const cfg: RegieConfigPayload = JSON.parse(JSON.stringify(entry.config));
    const mediaFiles: Record<string, string> = {};
    const usedPaths = new Set<string>();

    for (const ref of collectMediaRefs(cfg)) {
      let blob: Blob;
      try { blob = await downloadRegieConfigMedia(entry.id, ref.prefix); }
      catch { missing++; continue; }

      let base = safeFileName(ref.originalName || `${ref.label || ref.prefix}.${extFromMime(blob.type)}`);
      // Un nom sans extension ne se rechargerait pas : le serveur range les fichiers par extension.
      if (base.lastIndexOf('.') <= 0) base += `.${extFromMime(blob.type)}`;
      const dot  = base.lastIndexOf('.');
      const stem = dot > 0 ? base.slice(0, dot) : base;
      const ext  = dot > 0 ? base.slice(dot) : '';
      let rel = `${ref.folder}/${base}`, k = 2;
      while (usedPaths.has(rel)) rel = `${ref.folder}/${stem} (${k++})${ext}`;
      usedPaths.add(rel);

      zip.file(`${folder}/${rel}`, blob);
      mediaFiles[ref.prefix] = rel;
      files++;
    }

    cfg.mediaFiles = mediaFiles;
    zip.file(`${folder}/${folder}.json`, JSON.stringify(cfg, null, 2));
  }

  const out = await zip.generateAsync({ type: 'blob' });
  await saveBlob(out, entries.length === 1 ? safeFileName(entries[0].name) : `medias-${new Date().toISOString().slice(0, 10)}`, 'zip', target);
  return { files, missing };
}

// ── Styles ────────────────────────────────────────────────────────────────────

// Charte des fenêtres de la régie (2026-10-24) : mêmes valeurs que « Config pupitre »
// (PupitreConfigModal) — voile 72 % flouté 8 px, fond de panneau, filet à 8 %, rayon 16,
// accent indigo, textes 12–16 px. Partagé par les fenêtres de sauvegarde ET de chargement.
const S = {
  overlay: {
    position: 'fixed' as const, inset: 0, zIndex: 9999,
    background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(8px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
  },
  container: (width: number): React.CSSProperties => ({
    background: 'var(--color-Panel-niv1-BG)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 16, width, maxWidth: '100%',
    maxHeight: '88vh',
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative',
    boxShadow: '0 32px 80px rgba(0,0,0,0.85)',
  }),
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
    padding: '14px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0,
  },
  headerTitle: { fontSize: 16, fontWeight: 700, color: '#f1f5f9', display: 'flex', alignItems: 'center', gap: 12 },
  closeBtn:    { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', cursor: 'pointer', color: '#cbd5e1', width: 32, height: 32, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, padding: 0 } as React.CSSProperties,
  footer:      { padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, flexShrink: 0 },
  btnCancel:   { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 8, color: '#cbd5e1', fontSize: 13, fontWeight: 700, padding: '8px 18px', cursor: 'pointer' },
  btnPrimary:  { background: '#6366f1', border: '1px solid #818cf8', borderRadius: 8, color: '#ffffff', fontSize: 13, fontWeight: 700, padding: '8px 20px', cursor: 'pointer', boxShadow: '0 4px 14px rgba(99,102,241,0.35)' },
  btnIcon:     { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 8, color: '#cbd5e1', padding: '6px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'border-color 0.15s, color 0.15s' } as React.CSSProperties,
  sectionLabel:{ fontSize: 11, color: '#94a3b8', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' as const },
};

/** Icône de l'en-tête, en pastille indigo (comme « Config pupitre »). */
const HeaderIcon: React.FC<{ icon: React.ReactNode }> = ({ icon }) => (
  <span style={{ width: 32, height: 32, borderRadius: 9, background: 'rgba(99,102,241,0.16)', color: '#818cf8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
    {icon}
  </span>
);

// ── Checkbox ──────────────────────────────────────────────────────────────────

const Checkbox: React.FC<{
  label:     string;
  checked:   boolean;
  disabled?: boolean;
  plain?:    boolean;
  onChange?: (v: boolean) => void;
}> = ({ label, checked, disabled = false, plain = false, onChange }) => (
  <label style={plain ? {
    display: 'flex', alignItems: 'center', gap: 9,
    cursor: disabled ? 'not-allowed' : 'pointer',
    userSelect: 'none', opacity: disabled ? 0.5 : 1,
  } : {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '9px 12px', borderRadius: 8,
    background: checked ? (disabled ? 'rgba(99,102,241,0.08)' : 'rgba(99,102,241,0.12)') : 'rgba(255,255,255,0.03)',
    border: `1px solid ${checked ? (disabled ? 'rgba(129,140,248,0.25)' : 'rgba(129,140,248,0.45)') : 'rgba(255,255,255,0.08)'}`,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'background 0.15s, border-color 0.15s',
    userSelect: 'none', opacity: disabled ? 0.7 : 1,
  }}>
    <input
      type="checkbox" checked={checked} disabled={disabled}
      onChange={e => onChange?.(e.target.checked)}
      style={{ accentColor: '#6366f1', width: 16, height: 16, cursor: disabled ? 'not-allowed' : 'pointer', flexShrink: 0 }}
    />
    <span style={{ fontSize: 13, fontWeight: 600, color: checked ? '#f1f5f9' : '#94a3b8', transition: 'color 0.15s' }}>
      {label}
    </span>
  </label>
);

// ── FeaturesGrid ──────────────────────────────────────────────────────────────

interface FeatureRow {
  feature      : RegieConfigFeature;
  label        : string;
  checked      : boolean;
  disabled     : boolean;
  mediaChecked : boolean;
  mediaDisabled: boolean;
  onToggle     : () => void;
  onToggleMedia: (v: boolean) => void;
  /** Famille sans fichier (PiP) : pas de case « Avec médias ». */
  noMedia?     : boolean;
}

const FeaturesGrid: React.FC<{ rows: FeatureRow[] }> = ({ rows }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
    {rows.map(r => (
      <div
        key={r.feature}
        style={{
          display: 'flex', flexDirection: 'row', alignItems: 'center',
          justifyContent: 'space-between',
          padding: '7px 10px', borderRadius: 6,
          background: r.checked ? 'rgba(99,102,241,0.1)' : 'rgba(255,255,255,0.02)',
          border: `1px solid ${r.checked ? 'rgba(99,102,241,0.35)' : 'rgba(255,255,255,0.07)'}`,
        }}
      >
        <Checkbox plain label={r.label}     checked={r.checked}      disabled={r.disabled}      onChange={r.onToggle} />
        {!r.noMedia && <Checkbox plain label="Avec médias" checked={r.mediaChecked} disabled={r.mediaDisabled} onChange={r.onToggleMedia} />}
      </div>
    ))}
  </div>
);

// ── LoadOptions ───────────────────────────────────────────────────────────────

interface LoadOptions {
  features : Record<RegieConfigFeature, boolean>;
  medias   : Record<RegieConfigFeature, boolean>;
}

function defaultLoadOptions(payload: RegieConfigPayload): LoadOptions {
  return {
    features: {
      image: !!payload.features.image,
      son  : !!payload.features.son,
      video: !!payload.features.video,
      scene: !!payload.features.scene,
      pip  : !!payload.features.pip,
    },
    medias: {
      image: !!payload.features.withImages,
      son  : !!payload.features.withSounds,
      video: !!payload.features.withVideos,
      scene: !!payload.features.withScene,
      pip  : false,
    },
  };
}

function buildLoadRows(
  payload     : RegieConfigPayload,
  loadOptions : LoadOptions,
  toggleFeature: (f: RegieConfigFeature) => void,
  toggleMedia  : (f: RegieConfigFeature, v: boolean) => void,
): FeatureRow[] {
  const { features, medias } = loadOptions;
  return [
    {
      feature: 'image', label: 'Image',
      checked: features.image, disabled: !payload.features.image,
      mediaChecked: medias.image, mediaDisabled: !features.image || !payload.features.withImages,
      onToggle: () => toggleFeature('image'),
      onToggleMedia: v => toggleMedia('image', v),
    },
    {
      feature: 'son', label: 'Son',
      checked: features.son, disabled: !payload.features.son,
      mediaChecked: features.son && !!payload.features.withSounds, mediaDisabled: true,
      onToggle: () => toggleFeature('son'),
      onToggleMedia: () => {},
    },
    {
      feature: 'video', label: 'Vidéo',
      checked: features.video, disabled: !payload.features.video,
      mediaChecked: medias.video, mediaDisabled: !features.video || !payload.features.withVideos,
      onToggle: () => toggleFeature('video'),
      onToggleMedia: v => toggleMedia('video', v),
    },
    {
      feature: 'scene', label: 'Scène',
      checked: features.scene, disabled: !payload.features.scene,
      mediaChecked: medias.scene, mediaDisabled: !features.scene || !payload.features.withScene,
      onToggle: () => toggleFeature('scene'),
      onToggleMedia: v => toggleMedia('scene', v),
    },
  ];
}

// ── ZipMediaMap ───────────────────────────────────────────────────────────────

type ZipMediaMap = Map<string, Blob>;

// ── parseMediaFilename ────────────────────────────────────────────────────────

function parseMediaFilename(filename: string): { mediaType: string; slotId: string; role: string } | null {
  const dotIdx    = filename.lastIndexOf('.');
  const nameNoExt = dotIdx >= 0 ? filename.slice(0, dotIdx) : filename;
  const i1 = nameNoExt.indexOf('_');
  if (i1 < 0) return null;
  const i2 = nameNoExt.indexOf('_', i1 + 1);
  if (i2 < 0) return null;
  return {
    mediaType : nameNoExt.slice(0, i1),
    slotId    : nameNoExt.slice(i1 + 1, i2),
    role      : nameNoExt.slice(i2 + 1),
  };
}

function buildMediaFilenamePrefix(mediaType: string, slotId: string, role: string): string {
  return `${mediaType}_${slotId}_${role}`;
}

// ── resolveMedia helpers ──────────────────────────────────────────────────────

async function resolveMediaFromServer(
  regieConfigId : number,
  prefix        : string,
  mediaType     : 'image' | 'sound' | 'video' | 'scene',
  slotId        : string,
  role          : string,
  originalName? : string,
): Promise<{ url: string; instantMediaId: string } | null> {
  try {
    const blob   = await downloadRegieConfigMedia(regieConfigId, prefix);
    const result = await storeMediaBlob(blob, originalName || prefix, mediaType, slotId, role);
    return { url: result.objectUrl, instantMediaId: result.mediaId };
  } catch (e) {
    console.warn(`[RegieLoad] Échec download ${prefix}:`, e);
    return null;
  }
}

async function resolveMediaFromZip(
  zipMedia  : ZipMediaMap,
  prefix    : string,
  mediaType : 'image' | 'sound' | 'video' | 'scene',
  slotId    : string,
  role      : string,
  originalName? : string,
): Promise<{ url: string; instantMediaId: string } | null> {
  const entry = Array.from(zipMedia.entries()).find(([name]) => name.startsWith(prefix));
  if (!entry) return null;
  const [filename, blob] = entry;
  try {
    const result = await storeMediaBlob(blob, originalName || filename, mediaType, slotId, role);
    return { url: result.objectUrl, instantMediaId: result.mediaId };
  } catch (e) {
    console.warn(`[RegieLoad] Échec stockage ZIP ${filename}:`, e);
    return null;
  }
}

// ── buildResolvedPayload ──────────────────────────────────────────────────────
// Construit le payload avec url + instantMediaId résolus.
// Les features non cochées sont absentes du data retourné.

async function buildResolvedPayload(
  payload       : RegieConfigPayload,
  options       : LoadOptions,
  regieConfigId : number | null,
  zipMedia      : ZipMediaMap,
): Promise<RegieConfigPayload> {

  const useZip    = zipMedia.size > 0;
  const useServer = !useZip && regieConfigId !== null;

const resolveSimple = async (
  slot      : { id: string; url?: string; hasMedia?: boolean; originalName?: string },
  mediaType : 'image' | 'sound' | 'video',
  wantMedia : boolean,
): Promise<{ url: string; instantMediaId?: string }> => {
  // Vidéo HLS du compte : l'url sauvegardée suffit, aucun fichier à rapatrier.
  if (wantMedia && isStreamedMediaUrl(slot.url)) return { url: slot.url! };
  if (!wantMedia || !slot.hasMedia) return { url: '' };   // ← ajout !slot.hasMedia
  const prefix   = buildMediaFilenamePrefix(mediaType, slot.id, 'slot');
  const resolved = useZip
    ? await resolveMediaFromZip(zipMedia, prefix, mediaType, slot.id, 'slot', slot.originalName)
    : useServer
      ? await resolveMediaFromServer(regieConfigId!, prefix, mediaType, slot.id, 'slot', slot.originalName)
      : null;
  return resolved ?? { url: '' };
};

  const result: RegieConfigPayload = { ...payload, data: { ...payload.data } };

  // IMAGE
  if (options.features.image && payload.data.image) {
    result.data.image = {
      slots: await Promise.all(payload.data.image.slots.map(async slot => {
        const media = await resolveSimple(slot, 'image', options.medias.image);
        return { ...slot, url: media.url, instantMediaId: media.instantMediaId };
      })),
    };
  } else {
    delete result.data.image;
  }

  // SON (toujours avec médias quand coché)
  if (options.features.son && payload.data.son) {
    result.data.son = {
      slots: await Promise.all(payload.data.son.slots.map(async slot => {
        const media = await resolveSimple(slot, 'sound', true);
        return { ...slot, url: media.url, instantMediaId: media.instantMediaId };
      })),
    };
  } else {
    delete result.data.son;
  }

  // VIDÉO
  if (options.features.video && payload.data.video) {
    result.data.video = {
      slots: await Promise.all(payload.data.video.slots.map(async slot => {
        const media = await resolveSimple(slot, 'video', options.medias.video);
        return { ...slot, url: media.url, instantMediaId: media.instantMediaId };
      })),
    };
  } else {
    delete result.data.video;
  }

  // SCÈNE
  if (options.features.scene && payload.data.scene) {
    result.data.scene = {
      slots: await Promise.all(payload.data.scene.slots.map(async slot => {

        let bgUrl = '';
        let bgInstantMediaId: string | undefined;
        const bgType = slot.background.type as string;
        if (options.medias.scene && (bgType === 'image' || bgType === 'video' || bgType === 'sound') && slot.background.hasMedia) {
          const prefix   = buildMediaFilenamePrefix('scene', slot.id, 'background');
          const resolved = useZip
            ? await resolveMediaFromZip(zipMedia, prefix, 'scene', slot.id, 'background', slot.background.originalName)
            : useServer
              ? await resolveMediaFromServer(regieConfigId!, prefix, 'scene', slot.id, 'background', slot.background.originalName)
              : null;
          if (resolved) { bgUrl = resolved.url; bgInstantMediaId = resolved.instantMediaId; }
        }
        if (options.medias.scene && isStreamedMediaUrl(slot.background.url)) bgUrl = slot.background.url;

        const newIncrustations = await Promise.all(slot.incrustations.map(async inc => {
          let incUrl = '';
          let incInstantMediaId: string | undefined;
          const incType = inc.type as string;
          if (options.medias.scene && (incType === 'image' || incType === 'video' || incType === 'sound') && inc.hasMedia) {
            const role    = `incrustation-${inc.id}`;
            const prefix  = buildMediaFilenamePrefix('scene', slot.id, role);
            const resolved = useZip
              ? await resolveMediaFromZip(zipMedia, prefix, 'scene', slot.id, role, inc.originalName)
              : useServer
                ? await resolveMediaFromServer(regieConfigId!, prefix, 'scene', slot.id, role, inc.originalName)
                : null;
            if (resolved) { incUrl = resolved.url; incInstantMediaId = resolved.instantMediaId; }
          }
          if (options.medias.scene && isStreamedMediaUrl(inc.url)) incUrl = inc.url;
          return { ...inc, url: incUrl, instantMediaId: incInstantMediaId };
        }));

        return {
          ...slot,
          background   : { ...slot.background, url: bgUrl, instantMediaId: bgInstantMediaId },
          incrustations: newIncrustations,
        };
      })) as LayoutSlot[],
    };
  } else {
    delete result.data.scene;
  }

  // PiP : de la config pure, rien à télécharger.
  if (!(options.features.pip && payload.data.pip)) delete result.data.pip;

  return result;
}

// ── NameField ─────────────────────────────────────────────────────────────────
// Nom de la sauvegarde + signalement d'un nom déjà pris sur cette chaîne.

const NameField: React.FC<{
  value      : string;
  onChange   : (v: string) => void;
  duplicate  : boolean;
}> = ({ value, onChange, duplicate }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <span style={S.sectionLabel}>Nom de la sauvegarde</span>
    <input
      type="text" value={value} onChange={e => onChange(e.target.value)}
      className="rcm-field"
      style={{
        background: 'rgba(255,255,255,0.05)',
        border: `1px solid ${duplicate ? 'rgba(239,68,68,0.6)' : 'rgba(129,140,248,0.4)'}`,
        borderRadius: 8, color: '#f1f5f9', fontSize: 13, fontWeight: 600, height: 36, padding: '0 12px', outline: 'none',
        width: '100%', boxSizing: 'border-box',
      }}
    />
    {duplicate && (
      <span style={{ fontSize: 12, color: '#fca5a5' }}>
        Une sauvegarde porte déjà ce nom sur cette chaîne — choisissez-en un autre.
      </span>
    )}
  </div>
);

// ── SaveModal ─────────────────────────────────────────────────────────────────

type SavingStep = 'saving' | 'uploading' | 'zipping' | null;

const SAVING_LABELS: Record<NonNullable<SavingStep>, string> = {
  saving   : 'Sauvegarde…',
  uploading: 'Upload des médias…',
  zipping  : 'Génération du ZIP…',
};

const SaveModal: React.FC<SaveModalProps> = (props) => {
  const { channelName, channelUuid, userName, kind, onClose } = props;

  const [name,         setName]         = useState(() => defaultName(channelName, userName, kind));
  // Mediaboard : ce qu'on emporte. Les fichiers suivent toujours.
  const [features,     setFeatures]     = useState<Record<RegieConfigFeature, boolean>>({ image: true, son: true, video: true, scene: true, pip: true });
  const [exportFile,   setExportFile]   = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [savingStep,   setSavingStep]   = useState<SavingStep>(null);
  const [error,        setError]        = useState<string | null>(null);
  const [existing,     setExisting]     = useState<string[] | null>(null);

  useEffect(() => {
    getRegieConfigs(channelUuid)
      .then(all => setExisting(all.filter(c => c.name !== AUTOSAVE_NAME).map(c => normalizeName(c.name))))
      .catch(()  => setExisting([]));
  }, [channelUuid]);

  const duplicate = existing !== null && existing.includes(normalizeName(name));
  const nothingChosen = kind === 'media' && !Object.values(features).some(Boolean);
  const blocked = !name.trim() || duplicate || nothingChosen || saving;

  const handleSave = useCallback(async () => {
    if (blocked) return;

    // Destination du fichier exporté : le sélecteur doit s'ouvrir AU CLIC (geste utilisateur),
    // avant l'envoi des fichiers. ZIP si la sauvegarde contiendra des médias, sinon JSON.
    let target: SaveTarget | null = null;
    if (exportFile) {
      const withFiles =
           (features.image && props.imageSlots.some(sl => !!sl.url))
        || (features.son   && props.soundSlots.some(sl => !!sl.url))
        || (features.video && props.jingleSlots.some(sl => !!sl.url))
        || (features.scene && props.layoutSlots.some(sl => !!sl.background.url || sl.incrustations.some(ic => !!ic.url)));
      const picked = await pickSaveTarget(name.trim(), withFiles ? 'zip' : 'json');
      if (picked === 'cancelled') return;   // rien n'a encore été fait : on annule tout
      target = picked;
    }

    setSaving(true); setSavingStep('saving'); setError(null);

    try {
      // Le nom a pu être pris entre l'ouverture de la fenêtre et le clic.
      if (await nameAlreadyUsed(channelUuid, name)) {
        setExisting(prev => [...(prev ?? []), normalizeName(name)]);
        setSaving(false); setSavingStep(null);
        return;
      }

      const { imageSlots, soundSlots, jingleSlots, layoutSlots, pipSlots } = props;

      // Deck : pupitre + TOUS les médias, sans choix. Media : les familles cochées.
      const chosen: Record<RegieConfigFeature, boolean> = props.kind === 'deck'
        ? { image: features.image, son: features.son, video: features.video, scene: features.scene, pip: false }
        : { ...features, pip: false }; // PiP : plus sauvegardé (plus de bouton dans le deck)

      let pupitre: NonNullable<RegieConfigPayload['data']['pupitre']> | undefined;
      if (props.kind === 'deck') {
        // Liste des sources externes : relevée AU MOMENT de la sauvegarde. Si le
        // microservice ne répond pas, on sauvegarde sans plutôt que de renoncer.
        const externalSources = await props.getExternalSources().catch(() => null);
        const sourceAudioConfigs: Record<string, SourceAudioConfig> = {};
        props.deckSlots.filter(Boolean).forEach(sl => { sourceAudioConfigs[sl!.id] = props.getSourceAudioConfig(sl!.id); });
        pupitre = {
          deckSlots: serializeDeckSlots(props.deckSlots),
          // Un bouton du deck ne doit pas renvoyer à des médias absents de la sauvegarde :
          // on retire les raccourcis de chaque famille décochée.
          keyMap: (() => {
            const km: Partial<DeckKeyMap> = { ...props.keyMap };
            if (!chosen.image) delete km.image;
            if (!chosen.son)   delete km.sound;
            if (!chosen.video) delete km.jingle;
            if (!chosen.scene) delete km.layout;
            return km;
          })(),
          slotConfigs: props.slotConfigs, sourceAliases: props.sourceAliases,
          sourceAudioConfigs,
          selectedAudioDeviceIds: Object.fromEntries(props.selectedAudioDeviceIds),
          stereoVolumes: Object.fromEntries(props.stereoVolumes),
          audioEffectsMap: Object.fromEntries(props.audioEffectsMap),
          audioEffectConfigsMap: Object.fromEntries(props.audioEffectConfigsMap),
          videoEffectsSettingsMap: Object.fromEntries(props.videoEffectsSettingsMap),
          deviceTransport: props.deviceTransport,
          knownSourceIds: props.knownSourceIds,
          autoDirector: props.autoDirectorSettings,
          whipEncoding: props.whipEncoding,
          audioSwitch: props.audioSwitchState,
          ...(externalSources ? { externalSources } : {}),
        };
      }

      const payload: RegieConfigPayload = {
        kind: props.kind, name: name.trim(), channelUuid, originChannelUuid: channelUuid, date: new Date().toISOString(),
        features: {
          ...(pupitre ? { pupitre: true as const } : {}),
          ...(chosen.image ? { image: true, withImages: true } : {}),
          ...(chosen.son   ? { son:   true, withSounds: true } : {}),
          ...(chosen.video ? { video: true, withVideos: true } : {}),
          ...(chosen.scene ? { scene: true, withScene:  true } : {}),
          ...(chosen.pip   ? { pip:   true } : {}),
        },
        data: {
          ...(pupitre ? { pupitre } : {}),
          ...(chosen.image ? { image: { slots: imageSlots  } } : {}),
          ...(chosen.son   ? { son:   { slots: soundSlots  } } : {}),
          ...(chosen.video ? { video: { slots: jingleSlots } } : {}),
          ...(chosen.scene ? { scene: { slots: layoutSlots } } : {}),
          ...(chosen.pip   ? { pip:   { slots: pipSlots } } : {}),
        },
      };

      const saved = await saveRegieConfig(channelUuid, preparePayloadForSave(payload));

      // Les fichiers partent AVANT de rendre la main : une sauvegarde de médias
      // qui se ferme avant la fin de l'envoi serait une sauvegarde sans médias.
      const nonEmptyLayouts = layoutSlots.filter(sl => !!sl.background.url || sl.incrustations.some(ic => !!ic.url));
      const all: Promise<void>[] = [];
      if (chosen.image) all.push(...uploadRegieConfigMedia(saved.id, 'image', imageSlots.filter(sl => !!sl.url)));
      if (chosen.son)   all.push(...uploadRegieConfigMedia(saved.id, 'sound', soundSlots.filter(sl => !!sl.url)));
      if (chosen.video) all.push(...uploadRegieConfigMedia(saved.id, 'video', jingleSlots.filter(sl => !!sl.url)));
      if (chosen.scene && nonEmptyLayouts.length > 0) all.push(...uploadRegieConfigSceneMedia(saved.id, nonEmptyLayouts));
      if (all.length > 0) { setSavingStep('uploading'); await Promise.all(all); }

      // Fichier exporté : un ZIP (médias sous leur nom d'origine) s'il y a des médias, sinon le seul JSON.
      if (exportFile) {
        if (all.length > 0) { setSavingStep('zipping'); await exportMediaZip([saved], target); }
        else await downloadJson(payload, payload.name, target);
      }
      onClose();
    } catch (e: any) {
      setError(e.message ?? 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false); setSavingStep(null);
    }
  }, [blocked, name, features, exportFile, channelUuid, props, onClose]);

  const mediaChoices: Array<[RegieConfigFeature, string]> = [
    ['image', 'Images'], ['son', 'Sons'], ['video', 'Vidéos (jingles)'], ['scene', 'Scènes'],
  ];

  return ReactDOM.createPortal(
    <div style={S.overlay}>
      <style>{`.rcm-field:focus { border-color: #818cf8 !important; box-shadow: 0 0 0 3px rgba(99,102,241,0.25); }`}</style>
      <div style={S.container(560)} onClick={e => e.stopPropagation()}>
        <div style={S.header}>
          <div style={S.headerTitle}>
            <HeaderIcon icon={<Save style={{ width: 16, height: 16 }} />} />
            <span style={{ display: 'flex', flexDirection: 'column' }}>
              {kind === 'deck' ? 'Sauvegarder le deck' : 'Sauvegarder les médias'}
              <span style={{ fontSize: 12, fontWeight: 500, color: '#94a3b8' }}>
                {kind === 'deck' ? `Pupitre et médias de ${channelName}` : `Médias de ${channelName}`}
              </span>
            </span>
          </div>
          <button style={S.closeBtn} onClick={onClose} title="Fermer sans sauvegarder"><X style={{ width: 16, height: 16 }} /></button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1, padding: 20, display: 'flex', flexDirection: 'column', gap: 18 }}>
          <NameField value={name} onChange={setName} duplicate={duplicate} />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {/* Le pupitre d'un deck est toujours inclus : on ne choisit que les médias. */}
            {(
              <>
                <span style={S.sectionLabel}>Contenu</span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 14 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {mediaChoices.map(([f, label]) => (
                      <Checkbox key={f} label={label} checked={features[f]} onChange={v => setFeatures(p => ({ ...p, [f]: v }))} />
                    ))}
                  </div>
                  <span style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.5 }}>
                    Les fichiers sont inclus, avec leur nom d'origine.{kind === 'deck' ? ' Une famille décochée n\'est pas sauvegardée, ni les raccourcis de ses boutons.' : ''}
                  </span>
                </div>
              </>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                <input type="checkbox" checked={exportFile} onChange={e => setExportFile(e.target.checked)} style={{ accentColor: '#6366f1', width: 16, height: 16, cursor: 'pointer' }} />
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: exportFile ? '#c7d2fe' : '#94a3b8', fontWeight: 600 }}
                      title="Enregistre aussi la sauvegarde sur cet ordinateur (ZIP avec les médias, sinon JSON)">
                  <Download style={{ width: 14, height: 14 }} /> Exporter aussi le fichier
                </span>
              </label>
            </div>
          </div>

          {error && (
            <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.35)', borderRadius: 10, fontSize: 13, color: '#fecaca' }}>{error}</div>
          )}
        </div>

        <div style={S.footer}>
          <button style={S.btnCancel} onClick={onClose} disabled={saving}>Annuler</button>
          <button
            onClick={handleSave} disabled={blocked}
            style={{ ...S.btnPrimary, opacity: blocked ? 0.5 : 1, cursor: blocked ? 'not-allowed' : 'pointer' }}
          >
            {savingStep ? SAVING_LABELS[savingStep] : 'Sauvegarder'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ── LoadModal ─────────────────────────────────────────────────────────────────

interface UploadCandidate {
  payload   : RegieConfigPayload;
  zipMedia  : ZipMediaMap;
  mediaStats: { total: number; matched: number };
}

interface UploadState {
  fileName  : string;
  fileType  : 'json' | 'zip' | null;
  payload   : RegieConfigPayload | null;
  zipMedia  : ZipMediaMap;
  mediaStats: { total: number; matched: number } | null;
  error     : string;
  /** ZIP contenant plusieurs sauvegardes : on en choisit une. */
  candidates?   : UploadCandidate[];
  candidateIdx? : number;
}

type LoadingStep = 'saving' | 'uploading' | 'restoring' | null;

const LOADING_LABELS: Record<NonNullable<LoadingStep>, string> = {
  saving   : 'Sauvegarde…',
  uploading: 'Upload des médias…',
  restoring: 'Restauration des médias…',
};

const INITIAL_UPLOAD_STATE: UploadState = {
  fileName: '', fileType: null, payload: null, zipMedia: new Map(), mediaStats: null, error: '',
};

const LoadModal: React.FC<LoadModalProps> = ({ kind, channelUuid, onLoad, onClose }) => {
  const [library,       setLibrary]       = useState<RegieConfigRecord[]>([]);
  const [loading,       setLoading]       = useState(true);
  // 'choose' : écran d'accueil (Bibliothèque / Téléversement) ; les deux autres remplacent les boutons.
  const [tab,           setTab]           = useState<'choose' | 'library' | 'upload'>('choose');
  const [selectedEntry, setSelectedEntry] = useState<RegieConfigRecord | null>(null);
  const [loadOptions,   setLoadOptions]   = useState<LoadOptions | null>(null);

  const [uploadState,   setUploadState]   = useState<UploadState>(INITIAL_UPLOAD_STATE);
  const [loadingStep,   setLoadingStep]   = useState<LoadingStep>(null);
  const [loadError,     setLoadError]     = useState<string | null>(null);

  const [exporting,     setExporting]     = useState(false);
  const [exportNote,    setExportNote]    = useState<string | null>(null);

  const fileRef = useRef<HTMLInputElement>(null);
  const autoLoadedRef = useRef<RegieConfigPayload | null>(null);

  const handleSelectEntry = useCallback((entry: RegieConfigRecord | null) => {
    setSelectedEntry(entry);
    setLoadOptions(entry ? defaultLoadOptions(entry.config) : null);
  }, []);

  const toggleLoadFeature = useCallback((f: RegieConfigFeature) => {
    setLoadOptions(prev => {
      if (!prev) return prev;
      const next = { ...prev.features, [f]: !prev.features[f] };
      const nextMedias = { ...prev.medias };
      if (f === 'son') nextMedias.son = next.son;
      if (!next[f]) nextMedias[f] = false;
      return { features: next, medias: nextMedias };
    });
  }, []);

  const toggleLoadMedia = useCallback((f: RegieConfigFeature, v: boolean) => {
    setLoadOptions(prev => prev ? { ...prev, medias: { ...prev.medias, [f]: v } } : prev);
  }, []);

  useEffect(() => {
    setLoading(true);
    getRegieConfigs(channelUuid)
      .then(data => setLibrary(data.filter(c => c.name !== AUTOSAVE_NAME && belongsToKind(c.config, kind))))
      .catch(()  => setLibrary([]))
      .finally(  () => setLoading(false));
  }, [channelUuid, kind]);

  const handleDelete = useCallback(async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    try {
      await deleteRegieConfig(id);
      setLibrary(prev => prev.filter(e => e.id !== id));
      setSelectedEntry(prev => prev?.id === id ? null : prev);
    } catch {}
  }, []);

  const runExport = useCallback(async (entries: RegieConfigRecord[]) => {
    // Sélecteur ouvert AU CLIC, avant le téléchargement des médias depuis le serveur.
    const target = await pickSaveTarget(entries.length === 1 ? entries[0].name : `medias-${new Date().toISOString().slice(0, 10)}`, 'zip');
    if (target === 'cancelled') return;
    setExporting(true); setExportNote(null);
    try {
      const { files, missing } = await exportMediaZip(entries, target);
      setExportNote(`ZIP téléchargé — ${files} fichier${files > 1 ? 's' : ''}${missing ? `, ${missing} introuvable${missing > 1 ? 's' : ''} sur le serveur` : ''}.`);
    } catch (err: any) {
      setExportNote(`Export impossible : ${err?.message ?? 'erreur inconnue'}`);
    } finally {
      setExporting(false);
    }
  }, []);

  const handleDownload = useCallback((e: React.MouseEvent, entry: RegieConfigRecord) => {
    e.stopPropagation();
    // Avec des médias : un ZIP (fichiers sous leur nom d'origine) ; sans : le seul JSON.
    if (collectMediaRefs(entry.config).length > 0) void runExport([entry]);
    else void pickSaveTarget(entry.name, 'json').then(t => { if (t !== 'cancelled') return downloadJson(entry.config, entry.name, t); });
  }, [runExport]);

  // ── confirmLoad ─────────────────────────────────────────────────────────────
  // Résout les médias (download + IndexedDB) puis remonte via onLoad.
  // handleLoadConfig dans LiveStreamManager installe les slots.

  const confirmLoad = useCallback(async (
    payload       : RegieConfigPayload,
    options       : LoadOptions,
    regieConfigId : number | null,
    zipMedia      : ZipMediaMap,
  ) => {
    const resolved = await buildResolvedPayload(payload, options, regieConfigId, zipMedia);
    onLoad(resolved);
    onClose();
  }, [onLoad, onClose]);

  // ── Bibliothèque ──────────────────────────────────────────────────────────

  const handleLibraryLoad = useCallback(async () => {
    if (!selectedEntry || !loadOptions) return;
    setLoadingStep('restoring'); setLoadError(null);
    try {
      await confirmLoad(selectedEntry.config, loadOptions, selectedEntry.id, new Map());
    } catch (e: any) {
      setLoadError(e.message ?? 'Erreur lors du chargement');
      setLoadingStep(null);
    }
  }, [selectedEntry, loadOptions, confirmLoad]);

  // ── Fichier (JSON / ZIP) ──────────────────────────────────────────────────

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';   // permet de re-choisir le même fichier
    if (!file) return;
    autoLoadedRef.current = null;
    setUploadState(INITIAL_UPLOAD_STATE);
    setLoadError(null);
    setLoadOptions(null);

    const isJson = file.name.endsWith('.json');
    const isZip  = file.name.endsWith('.zip');

    if (!isJson && !isZip) {
      setUploadState(prev => ({ ...prev, fileName: file.name, error: 'Format non supporté. Veuillez sélectionner un fichier .json ou .zip.' }));
      return;
    }

    if (isJson) {
      const reader = new FileReader();
      reader.onload = ev => {
        try {
          const parsed = JSON.parse(ev.target?.result as string) as RegieConfigPayload;
          if (!parsed.name || !parsed.features || !parsed.data) throw new Error('Format invalide');
          if (!acceptsFile(parsed, kind)) throw Object.assign(new Error(wrongFileMessage(kind)), { kindMismatch: true });
          setUploadState({ fileName: file.name, fileType: 'json', payload: parsed, zipMedia: new Map(), mediaStats: null, error: '' });
          setLoadOptions(defaultLoadOptions(parsed));
        } catch (err: any) {
          setUploadState(prev => ({ ...prev, fileName: file.name, fileType: 'json', error: err?.kindMismatch ? err.message : 'Fichier JSON invalide ou corrompu.' }));
        }
      };
      reader.readAsText(file);
    } else {
      const reader = new FileReader();
      reader.onload = async ev => {
        try {
          const zip = await JSZip.loadAsync(ev.target?.result as ArrayBuffer);

          // Une sauvegarde = un fichier .json (racine ou un sous-dossier) + les médias de son dossier.
          const jsonFiles = Object.values(zip.files).filter(f =>
            !f.dir && f.name.endsWith('.json') && f.name.split('/').filter(Boolean).length <= 2);
          if (jsonFiles.length === 0) throw new Error('Aucun fichier .json trouvé dans le ZIP.');

          const candidates: UploadCandidate[] = [];
          let mismatch: string | null = null;

          for (const jsonFile of jsonFiles) {
            let parsed: RegieConfigPayload;
            try { parsed = JSON.parse(await jsonFile.async('string')) as RegieConfigPayload; } catch { continue; }
            if (!parsed.name || !parsed.features || !parsed.data) continue;
            if (!acceptsFile(parsed, kind)) { mismatch = wrongFileMessage(kind); continue; }

            const folder = jsonFile.name.includes('/') ? jsonFile.name.slice(0, jsonFile.name.lastIndexOf('/') + 1) : '';
            const zipMedia: ZipMediaMap = new Map();
            let total = 0, matched = 0;

            if (parsed.mediaFiles) {
              // ZIP exporté d'ici : fichiers sous leur nom d'origine, rattachés par `mediaFiles`.
              for (const [prefix, rel] of Object.entries(parsed.mediaFiles)) {
                total++;
                const entry = zip.file(folder + rel);
                if (!entry) continue;
                const dot = rel.lastIndexOf('.');
                zipMedia.set(prefix + (dot > 0 ? rel.slice(rel.lastIndexOf('.')) : ''), await entry.async('blob'));
                matched++;
              }
            } else {
              // ZIP produit par le serveur : fichiers aux noms techniques, en sous-dossiers.
              for (const [path, zipEntry] of Object.entries(zip.files)) {
                if (zipEntry.dir || !path.startsWith(folder) || path.endsWith('.json')) continue;
                const parts = path.slice(folder.length).split('/').filter(Boolean);
                if (parts.length < 1 || (folder === '' && parts.length < 2)) continue;
                total++;
                const filename = parts[parts.length - 1];
                if (parseMediaFilename(filename)) {
                  zipMedia.set(filename, await zipEntry.async('blob'));
                  matched++;
                }
              }
            }

            const payload = { ...parsed };
            delete payload.mediaFiles;   // propre à l'export, ne doit pas être enregistré
            candidates.push({ payload, zipMedia, mediaStats: { total, matched } });
          }

          if (candidates.length === 0) throw new Error(mismatch ?? 'Format invalide');

          const first = candidates[0];
          setUploadState({
            fileName: file.name, fileType: 'zip',
            payload: first.payload, zipMedia: first.zipMedia, mediaStats: first.mediaStats, error: '',
            candidates, candidateIdx: 0,
          });
          setLoadOptions(defaultLoadOptions(first.payload));
        } catch (err: any) {
          setUploadState(prev => ({ ...prev, fileName: file.name, fileType: 'zip', error: err.message ?? 'Fichier ZIP invalide ou corrompu.' }));
        }
      };
      reader.readAsArrayBuffer(file);
    }
  }, [kind]);

  const handleCandidateChange = useCallback((idx: number) => {
    setUploadState(prev => {
      const c = prev.candidates?.[idx];
      if (!c) return prev;
      setLoadOptions(defaultLoadOptions(c.payload));
      return { ...prev, payload: c.payload, zipMedia: c.zipMedia, mediaStats: c.mediaStats, candidateIdx: idx };
    });
  }, []);

  const handleUploadLoad = useCallback(async () => {
    const { payload, fileType, zipMedia } = uploadState;
    if (!payload || !loadOptions) return;
    setLoadingStep('saving'); setLoadError(null);
    try {
      // La copie enregistrée sur CETTE chaîne garde la chaîne d'origine : son `channelUuid`
      // est réécrit par l'enregistrement, et l'on ne saurait plus d'où viennent ses sources.
      const withOrigin: RegieConfigPayload = { ...payload, originChannelUuid: payload.originChannelUuid ?? payload.channelUuid };
      // Payload d'un fichier : url déjà vides, médias existants → on garde `hasMedia`.
      const saved = await saveRegieConfig(channelUuid, preparePayloadForSave(withOrigin, true));

      if (fileType === 'zip' && zipMedia.size > 0) {
        setLoadingStep('uploading');
        const { API_URLS }              = await import('../../../backendServices/urls');
        const { FileUploaderWithStore } = await import('../../../backendServices/fileManagment/uploadWithStore');

        const uploadPromises: Promise<void>[] = [];
for (const [filename, blob] of Array.from(zipMedia.entries())) {
          const parsed2 = parseMediaFilename(filename);
          if (!parsed2) continue;
          const { mediaType, slotId, role } = parsed2;
          let fileType2 = '';
          if (mediaType === 'scene') {
            const mt = blob.type;
            if (mt.startsWith('image/'))      fileType2 = 'image';
            else if (mt.startsWith('video/')) fileType2 = 'video';
            else if (mt.startsWith('audio/')) fileType2 = 'sound';
          }
          const metadata = { regieConfigId: saved.id, mediaType, slotId, role, filename, fileType: fileType2 };
          const uploadConfig = {
            uploadChunkUrl: API_URLS.uploadRegieMediaChunk, finalizeUploadUrl: API_URLS.finalizeRegieMediaUpload,
            chunkSize: 5 * 1024 * 1024, maxRetries: 3, enableResume: true,
            entityId: saved.id, entityType: 'regie_media', autoStart: false, metadata,
          };
          const file2    = new File([blob], filename, { type: blob.type });
          const uploader = new FileUploaderWithStore(file2, uploadConfig);
          uploadPromises.push(uploader.start(metadata as any).catch(e => {
            console.warn(`[RegieLoad] Upload ZIP échoué pour ${filename}:`, e);
          }));
        }
        Promise.all(uploadPromises).catch(console.warn); // fire-and-forget
      }

      setLoadingStep('restoring');
      await confirmLoad(withOrigin, loadOptions, saved.id, zipMedia);

    } catch (err: any) {
      setLoadError(err.message ?? 'Erreur lors du chargement');
      setLoadingStep(null);
    }
  }, [uploadState, channelUuid, loadOptions, confirmLoad]);

  const isLoading = loadingStep !== null;

  // Téléversement : dès que le fichier est lu, il est chargé (options par défaut).
  // Un ZIP à plusieurs sauvegardes attend le choix de l'utilisateur.
  useEffect(() => {
    if (tab !== 'choose' || isLoading || loadError || uploadState.error) return;
    if (!uploadState.payload || !loadOptions) return;
    // ZIP à plusieurs sauvegardes : seul cas où l'on affiche la vue de choix.
    if ((uploadState.candidates?.length ?? 0) > 1) { setTab('upload'); return; }
    if (autoLoadedRef.current === uploadState.payload) return;
    autoLoadedRef.current = uploadState.payload;
    void handleUploadLoad();
  }, [tab, isLoading, loadError, uploadState, loadOptions, handleUploadLoad]);

  return ReactDOM.createPortal(
    <div style={S.overlay}>
      <style>{`
        .rcm-tab { cursor:pointer; background:none; border:none; padding:8px 16px; font-size:12px; font-weight:700; transition:color 0.15s; }
        .rcm-tab:hover { color:#a5b4fc; }
        .rcm-lib-row { transition: background 0.12s, border-color 0.12s; cursor: pointer; }
        .rcm-lib-row:hover { background: rgba(255,255,255,0.04) !important; }
      `}</style>
      <div style={S.container(680)} onClick={e => e.stopPropagation()}>

        <div style={S.header}>
          <div style={S.headerTitle}>
            <HeaderIcon icon={<FolderOpen style={{ width: 16, height: 16 }} />} />
            Chargement d'une config
          </div>
          <button style={S.closeBtn} onClick={onClose} title="Fermer"><X style={{ width: 16, height: 16 }} /></button>
        </div>

        {/* Fichier caché, hors des vues : « Téléversement » ouvre le sélecteur directement. */}
        <input ref={fileRef} type="file" accept=".json,.zip" onChange={handleFileSelect} style={{ display: 'none' }} />

        <div style={{ overflowY: 'auto', flex: 1, padding: '14px 16px' }}>

          {tab === 'choose' && (
            <div style={{ display: 'flex', gap: 12, padding: '24px 0', justifyContent: 'center' }}>
              <button style={{ ...S.btnCancel, padding: '14px 28px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 10 }} onClick={() => setTab('library')}>
                <Library style={{ width: 18, height: 18 }} />
                Bibliothèque
              </button>
              <button style={{ ...S.btnCancel, padding: '14px 28px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 10 }} onClick={() => fileRef.current?.click()}>
                <Upload style={{ width: 18, height: 18 }} />
                À partir d'un fichier
              </button>
            </div>
          )}
          {tab === 'choose' && (isLoading || uploadState.error || loadError) && (
            <div style={{ textAlign: 'center', fontSize: 12, color: (uploadState.error || loadError) ? '#fca5a5' : '#94a3b8' }}>
              {isLoading ? LOADING_LABELS[loadingStep!] : (uploadState.error || loadError)}
            </div>
          )}

          {tab === 'library' && (
            loading ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Chargement…</div>
            ) : library.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Aucune sauvegarde disponible</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {library.map(entry => {
                  const isSel = selectedEntry?.id === entry.id;
                  return (
                    <div
                      key={entry.id} className="rcm-lib-row"
                      onClick={() => handleSelectEntry(isSel ? null : entry)}
                      style={{
                        padding: '12px 14px', borderRadius: 8,
                        background: isSel ? 'rgba(255,255,255,0.16)' : 'rgba(255,255,255,0.02)',
                        border: `1px solid ${isSel ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.07)'}`,
                        boxShadow: isSel ? '0 0 0 1px rgba(255,255,255,0.25)' : 'none',
                        display: 'flex', flexDirection: 'column', gap: 8,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: isSel ? '#ffffff' : '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {entry.name}
                          </div>
                          <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace', marginTop: 2 }}>
                            {formatDate(entry.createdAt)}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                          <button
                            style={{ ...S.btnIcon, color: '#86efac', borderColor: 'rgba(34,197,94,0.35)', background: 'rgba(34,197,94,0.07)', padding: '7px 9px', opacity: exporting ? 0.5 : 1 }}
                            disabled={exporting}
                            title={collectMediaRefs(entry.config).length > 0 ? 'Télécharger en ZIP (médias avec leur nom d\'origine)' : 'Télécharger (JSON)'} onClick={e => handleDownload(e, entry)}
                          >
                            <Download style={{ width: 20, height: 20 }} />
                          </button>
                          <button
                            style={{ ...S.btnIcon, color: '#fca5a5', borderColor: 'rgba(239,68,68,0.35)', background: 'rgba(239,68,68,0.07)', padding: '7px 9px' }}
                            title="Supprimer" onClick={e => handleDelete(e, entry.id)}
                          >
                            <Trash2 style={{ width: 20, height: 20 }} />
                          </button>
                        </div>
                      </div>

                    </div>
                  );
                })}
              </div>
            )
          )}

          {tab === 'upload' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={S.sectionLabel}>Fichier de configuration</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {uploadState.error && (
                    <button style={{ ...S.btnCancel, padding: '6px 14px' }} disabled={isLoading} onClick={() => fileRef.current?.click()}>
                      Choisir un autre fichier…
                    </button>
                  )}
                  {isLoading && <span style={{ fontSize: 12, color: '#94a3b8' }}>{LOADING_LABELS[loadingStep!]}</span>}
                  {uploadState.fileName && (
                    <span style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 340 }}>
                      {uploadState.fileType === 'zip' ? '🗜 ' : '📄 '}{uploadState.fileName}
                    </span>
                  )}
                </div>
                {uploadState.error && <span style={{ fontSize: 11, color: '#fca5a5' }}>{uploadState.error}</span>}
              </div>

              {uploadState.candidates && uploadState.candidates.length > 1 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span style={S.sectionLabel}>Ce ZIP contient {uploadState.candidates.length} sauvegardes — laquelle charger ?</span>
                  <select
                    value={uploadState.candidateIdx ?? 0} disabled={isLoading}
                    onChange={e => handleCandidateChange(Number(e.target.value))}
                    style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(99,102,241,0.35)', borderRadius: 5, color: '#fff', fontSize: 12, padding: '7px 10px', fontFamily: 'monospace' }}
                  >
                    {uploadState.candidates.map((c, i) => <option key={i} value={i} style={{ color: '#000' }}>{c.payload.name}</option>)}
                  </select>
                </div>
              )}

              {uploadState.payload && loadOptions && (
                <div style={{ padding: '12px 14px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: 8, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0' }}>{uploadState.payload.name}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace', marginTop: 2 }}>{formatDate(uploadState.payload.date)}</div>
                    {uploadState.fileType === 'zip' && uploadState.mediaStats && (
                      <div style={{ marginTop: 6, padding: '5px 8px', background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 5, fontSize: 11, color: '#86efac' }}>
                        {uploadState.mediaStats.matched === uploadState.mediaStats.total
                          ? `✓ ${uploadState.mediaStats.matched} fichier${uploadState.mediaStats.matched > 1 ? 's' : ''} média reconnu${uploadState.mediaStats.matched > 1 ? 's' : ''}`
                          : `${uploadState.mediaStats.matched} / ${uploadState.mediaStats.total} fichiers médias reconnus`}
                        {uploadState.mediaStats.matched < uploadState.mediaStats.total && (
                          <span style={{ color: 'rgba(251,191,36,0.9)', marginLeft: 8 }}>
                            ({uploadState.mediaStats.total - uploadState.mediaStats.matched} non reconnu{uploadState.mediaStats.total - uploadState.mediaStats.matched > 1 ? 's' : ''})
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 6, padding: '8px 10px' }}>
                    {kind === 'deck' && uploadState.payload.data.pupitre && <Checkbox label="Pupitre" checked={true} disabled={true} />}
                    {hasMediaFeatures(uploadState.payload) && (
                      <FeaturesGrid
                        rows={buildLoadRows(uploadState.payload, loadOptions, toggleLoadFeature, toggleLoadMedia)}
                      />
                    )}
                  </div>
                </div>
              )}

              {loadError && (
                <div style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, fontSize: 12, color: '#fca5a5' }}>{loadError}</div>
              )}
            </div>
          )}
        </div>

        <div style={S.footer}>
          {tab !== 'choose' && (
            <button style={{ ...S.btnCancel, marginRight: 'auto' }} onClick={() => { setTab('choose'); handleSelectEntry(null); setUploadState(INITIAL_UPLOAD_STATE); setLoadOptions(null); setLoadError(null); }} disabled={isLoading}>
              ← Retour
            </button>
          )}
          {tab === 'library' && exportNote && <span style={{ fontSize: 11, color: '#94a3b8', marginRight: 'auto', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{exportNote}</span>}
          <button style={S.btnCancel} onClick={onClose} disabled={isLoading}>Fermer</button>

          {tab === 'library' && selectedEntry && loadOptions && (
            <button
              style={{ ...S.btnPrimary, opacity: isLoading ? 0.4 : 1, cursor: isLoading ? 'not-allowed' : 'pointer' }}
              disabled={isLoading}
              onClick={handleLibraryLoad}
            >
              {loadingStep ? LOADING_LABELS[loadingStep] : 'Charger'}
            </button>
          )}

          {tab === 'upload' && (
            <button
              style={{ ...S.btnPrimary, opacity: (uploadState.payload && loadOptions && !isLoading) ? 1 : 0.4, cursor: (uploadState.payload && loadOptions && !isLoading) ? 'pointer' : 'not-allowed' }}
              disabled={!uploadState.payload || !loadOptions || isLoading}
              onClick={handleUploadLoad}
            >
              {loadingStep ? LOADING_LABELS[loadingStep] : 'Charger'}
            </button>
          )}
        </div>

      </div>
    </div>,
    document.body
  );
};

// ── Export principal ──────────────────────────────────────────────────────────

export const RegieConfigModal: React.FC<RegieConfigModalProps> = (props) => {
  if (props.mode === 'save') return <SaveModal {...props} />;
  return <LoadModal {...props} />;
};

export default RegieConfigModal;